import { ImageUploadButtons } from "@shared/components/ImageUploadButtons";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { ConfirmationModal } from "../../../shared/components/ConfirmationModal";
import {
  DebouncedInput,
  DebouncedRadio,
  DebouncedTextarea,
} from "../../../shared/components/inputs";
import { preserveScrollPosition } from "../pages/TestEditor";
import { TestQuestion, TestQuestionOption } from "../types/test-detail.types";
import { DEBOUNCE_DELAY } from "../utils/constants";

interface QuestionEditorProps {
  question: TestQuestion;
  questionIndex: number;
  onUpdate: (question: TestQuestion) => void;
  onRemove: () => void;
  userId: string;
  isExpanded?: boolean;
  isIndividuallyExpanded?: boolean;
  onToggleExpansion?: (questionId: string, isExpanded: boolean) => void;
}

function QuestionEditor({
  question,
  questionIndex,
  onUpdate,
  onRemove,
  userId,
  isExpanded: controlledExpanded,
  isIndividuallyExpanded,
  onToggleExpansion,
}: QuestionEditorProps) {
  // Use local state for expansion control
  const [localExpanded, setLocalExpanded] = useState(true);

  // Update local state when controlled state changes, but only on initial render and explicit collapse/expand all actions
  useEffect(() => {
    if (controlledExpanded !== undefined) {
      setLocalExpanded(controlledExpanded);
    }
  }, [controlledExpanded]);

  // If individual expansion state is provided, use it instead
  const isExpanded =
    isIndividuallyExpanded !== undefined
      ? isIndividuallyExpanded
      : localExpanded;

  // Function to handle expansion toggle
  const handleToggleExpansion = () => {
    // Update local state
    const newExpandedState = !isExpanded;
    setLocalExpanded(newExpandedState);

    // If onToggleExpansion callback is provided, call it
    if (onToggleExpansion) {
      onToggleExpansion(question.id, newExpandedState);
    }
  };
  const optionFileInputRefs = useRef<{
    [key: string]: HTMLInputElement | null;
  }>({});

  // State for confirmation modal
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Update question text with debounce
  const updateQuestionText = useDebouncedCallback((text: string) => {
    onUpdate({
      ...question,
      text,
    });
  }, DEBOUNCE_DELAY);

  // Update question number with debounce
  const updateQuestionNumber = useDebouncedCallback((number: number) => {
    onUpdate({
      ...question,
      number,
    });
  }, DEBOUNCE_DELAY);

  // Update line references with debounce
  const updateLineReferences = useDebouncedCallback(
    (field: "start" | "end", value: number) => {
      onUpdate({
        ...question,
        lineReferences: {
          ...question.lineReferences,
          [field]: value,
        },
      });
    },
    DEBOUNCE_DELAY
  );

  // Update correct answer with debounce
  const updateCorrectAnswer = useDebouncedCallback((correctAnswer: string) => {
    onUpdate({
      ...question,
      correctAnswer,
    });
  }, DEBOUNCE_DELAY);

  // Update option text with debounce
  const updateOptionText = useCallback(
    (optionId: string, text: string) => {
      const updatedOptions = question.options.map((option) =>
        option.id === optionId ? { ...option, text } : option
      );

      onUpdate({
        ...question,
        options: updatedOptions,
      });
    },
    [question, onUpdate]
  );

  // Debounced version for text inputs
  const debouncedUpdateOptionText = useDebouncedCallback(
    (optionId: string, text: string) => {
      updateOptionText(optionId, text);
    },
    DEBOUNCE_DELAY
  );

  // Add option
  const addOption = () => {
    // Generate next option letter
    const lastOption = question.options[question.options.length - 1];
    const lastOptionLetter: string | undefined = lastOption?.option;
    const nextOptionLetter = String.fromCharCode(
      lastOptionLetter?.charCodeAt(0) + 1 || "A".charCodeAt(0)
    );

    const newOption: TestQuestionOption = {
      id: nextOptionLetter,
      option: nextOptionLetter,
      text: ``,
    };

    onUpdate({
      ...question,
      options: [...question.options, newOption],
    });
  };

  // Remove option
  const removeOption = (optionId: string) => {
    const updatedOptions = question.options.filter(
      (option) => option.id !== optionId
    );

    // If we're removing the correct answer, update it to the first available option
    let updatedCorrectAnswer = question.correctAnswer;
    if (question.correctAnswer === optionId && updatedOptions.length > 0) {
      updatedCorrectAnswer = updatedOptions[0].id;
    }

    onUpdate({
      ...question,
      options: updatedOptions,
    });
  };

  // Image upload functionality has been moved to the ImageUploadButtons component

  // Prompt for question removal
  const promptRemoveQuestion = () => {
    setShowDeleteConfirmation(true);
  };

  // Confirm question removal
  const confirmRemoveQuestion = () => {
    onRemove();
    setShowDeleteConfirmation(false);
  };

  // This function is no longer needed as we're using the ImageUploadButtons component

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-md w-full">
      {/* Question Header */}
      <div className="bg-gray-50 dark:bg-gray-700 p-3 flex justify-between items-center w-full">
        <div className="flex items-center min-w-0 flex-1 overflow-hidden">
          <button
            type="button"
            onClick={(e) => {
              preserveScrollPosition(() => {
                handleToggleExpansion();
              });
            }}
            className="mr-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex-shrink-0"
          >
            <svg
              className={`w-4 h-4 transform ${isExpanded ? "rotate-90" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5l7 7-7 7"
              ></path>
            </svg>
          </button>
          <div className="flex items-center gap-2 min-w-0 overflow-hidden">
            <span className="font-medium text-gray-800 dark:text-gray-200 mr-2 flex-shrink-0">
              Question {question.number || ""}
            </span>
            <DebouncedInput
              type="number"
              min={1}
              value={question.number || 1}
              onChange={(value) => updateQuestionNumber(value as number)}
              className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white flex-shrink-0"
            />
            {question.text && (
              <span className="text-gray-500 dark:text-gray-400 truncate max-w-[100%] overflow-hidden">
                {question.text}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={promptRemoveQuestion}
          className="px-2 py-1 text-xs bg-red-500 hover:bg-red-600 text-white rounded-md flex-shrink-0 ml-2"
        >
          Remove
        </button>
      </div>

      {isExpanded && (
        <div className="p-4">
          {/* Question Text */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Question Text
            </label>
            <div className="flex flex-col">
              <DebouncedTextarea
                value={question.text || ""}
                onChange={(value) => updateQuestionText(value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                placeholder="Enter question text"
                showMathKeyboard
              />
              <div className="mt-2">
                <ImageUploadButtons
                  onImageUploaded={(formattedTag) => {
                    const updatedText = question.text
                      ? `${question.text}\n${formattedTag}`
                      : formattedTag;

                    onUpdate({
                      ...question,
                      text: updatedText,
                    });
                  }}
                />
              </div>
            </div>
          </div>

          {/* Line References */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Line References (Optional)
            </label>
            <div className="flex items-center space-x-2">
              <div className="flex items-center">
                <span className="text-sm text-gray-500 dark:text-gray-400 mr-1">
                  Start:
                </span>
                <DebouncedInput
                  type="number"
                  min={1}
                  value={question.lineReferences?.start || ""}
                  onChange={(value) => {
                    if (value !== "") {
                      updateLineReferences("start", value as number);
                    }
                  }}
                  className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Start"
                />
              </div>
              <div className="flex items-center">
                <span className="text-sm text-gray-500 dark:text-gray-400 mr-1">
                  End:
                </span>
                <DebouncedInput
                  type="number"
                  min={1}
                  value={question.lineReferences?.end || ""}
                  onChange={(value) => {
                    if (value !== "") {
                      updateLineReferences("end", value as number);
                    }
                  }}
                  className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  placeholder="End"
                />
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Options
              </label>
              <button
                onClick={addOption}
                className="px-2 py-1 text-xs bg-indigo-500 hover:bg-indigo-600 text-white rounded-md"
              >
                Add Option
              </button>
            </div>
            {question.options.length === 0 ? (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Correct Answer
                </label>
                <DebouncedTextarea
                  value={question.correctAnswer || ""}
                  onChange={(value) => updateCorrectAnswer(value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter the correct answer"
                  rows={4}
                  showMathKeyboard
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Enter the exact answer that will be considered correct.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {question.options.map((option) => (
                  <div
                    key={option.id}
                    className="flex items-start border border-gray-200 dark:border-gray-700 rounded-md p-3"
                  >
                    <div className="flex-shrink-0 mr-3">
                      <DebouncedRadio
                        id={`option-${option.id}`}
                        name={`question-${question.id}-correct`}
                        value={option.id}
                        checked={question.correctAnswer === option.id}
                        onChange={(value) => updateCorrectAnswer(value)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600"
                      />
                    </div>
                    <div className="flex-grow">
                      <div className="flex items-center mb-1">
                        <label
                          htmlFor={`option-${option.id}`}
                          className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2"
                        >
                          Option {option.option}
                        </label>
                        {question.correctAnswer === option.id && (
                          <span className="text-xs bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100 px-2 py-0.5 rounded-full">
                            Correct Answer
                          </span>
                        )}
                      </div>
                      <DebouncedTextarea
                        value={option.text || ""}
                        onChange={(value) =>
                          debouncedUpdateOptionText(option.id, value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                        placeholder={`Text for option ${option.option}`}
                        rows={4}
                        showMathKeyboard
                      />
                      <div className="mt-2 flex items-center justify-between">
                        <div>
                          <ImageUploadButtons
                            onImageUploaded={(formattedTag) => {
                              const updatedText = option.text
                                ? `${option.text}\n${formattedTag}`
                                : formattedTag;

                              updateOptionText(option.id, updatedText);
                            }}
                            addImageText="Add Image"
                            pasteImageText="Paste Image"
                            helpText=""
                            autoFormatImageTag={true}
                            imageTagFormat='[img src="{url}"]'
                          />
                        </div>
                        <button
                          onClick={() => removeOption(option.id)}
                          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-xs"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Confirmation Modal for Question Deletion */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={confirmRemoveQuestion}
        title="Delete Question"
        message={`Are you sure you want to delete Question ${question.number}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
}

// Memoize the component to prevent unnecessary re-renders
export default memo(QuestionEditor);
